import {
  TextInput,
  TextAreaInput,
  SelectInput,
  MultiCheckbox,
  MultipleChoice,
  LinearScaleInput,
  DateOfBirth,
  NumericInput,
} from '../../../components/Forms/FormixComponents';
import * as Yup from 'yup';
import {Geolocator, NameRecordingInput} from './AboutFormCustomInputs';
import type {FormikErrors, FormikHelpers, FormikTouched, FormikValues} from 'formik';
import {
  FormField,
  GENDER_OPTIONS,
  GenderQuestionId,
  MINIMUM_APPLICANT_AGE,
  getAgeFromDateOfBirth,
  isCompleteDateOfBirth,
} from '../firebase/models/formData';
import { useTranslateFunction } from 'contexts/translation/TranslationContext';
import type { TranslationFunction } from 'components/translation/utils';

/**
 * Single stable component for all form fields. Using one component reference
 * (instead of createAboutFormSection per field) prevents React from unmounting
 * inputs on re-render, which fixes Android keyboard disappearing when typing.
 */
const AboutFormFieldRenderer = ({
  name,
  field,
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
}: {
  name: string;
  field: FormField;
  values: FormikValues;
  touched: FormikTouched<FormikValues>;
  errors: FormikErrors<FormikValues>;
  setFieldValue: FormikHelpers<FormikValues>['setFieldValue'];
  setFieldTouched: (name: string, touched?: boolean) => void;
}) => {
  const t = useTranslateFunction('application');

  switch (field.type) {
    case 'text':
      return (
        <TextInput
          label={field.question}
          name={name}
          placeholder={field.placeholder}
        />
      );

    case 'longtext':
      return (
        <TextAreaInput
          label={field.question}
          description={field.description}
          name={name}
          placeholder={field.placeholder}
        />
      );

    case 'numeric':
      return (
        <NumericInput
          label={field.question}
          name={name}
          placeholder={field.placeholder}
        />
      );

    case 'multicheckbox': {
      return (
        <MultiCheckbox
          name={name}
          label={field.question}
          description={field.description}
          options={field.options.map(option => ({
            label: option,
            value: option,
          }))}
          allowOther={field.hasOther}
          otherLabel={t('Other', {forceString: true}) as string}
        />
      );
    }

    case 'singlecheckbox':
      return (
        <MultiCheckbox
          name={name}
          description={field.description}
          label={field.question}
          options={[{label: field.checkboxLabel, value: 'yes'}]}
        />
      );

    case 'dropdown': {
      if (field.id === GenderQuestionId) {
        return (
          <SelectInput label={field.question} name={name}>
            {/* Keep the empty option: without it the browser pre-selects a real
                choice and the required check passes on a value nobody picked. */}
            <option value="" disabled hidden>
              {t('Select an option')}
            </option>
            {GENDER_OPTIONS.map(option => (
              <option key={option} value={option}>
                {t(option)}
              </option>
            ))}
          </SelectInput>
        );
      }
      return (
        <SelectInput label={field.question} name={name}>
          <option value="" disabled hidden>
            {t('Select an option')}
          </option>
          {field.options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </SelectInput>
      );
    }

    case 'radio':
      return (
        <MultipleChoice
          label={field.question}
          name={name}
          options={field.options.map(option => ({label: option, value: option}))}
        />
      );

    case 'linear_scale':
      return (
        <LinearScaleInput
          label={field.question}
          name={name}
          min={field.min}
          max={field.max}
          minLabel={field.minLabel}
          maxLabel={field.maxLabel}
        />
      );

    case 'date':
      return (
        <DateOfBirth
          name={name}
          setFieldValue={setFieldValue}
          question={field.question}
        />
      );

    case 'text_display':
      return (
        <div className="question-spacing">
          <h3>{field.question}</h3>
          <p>{field.subtext}</p>
        </div>
      );

    case 'audio_input':
      return (
        <div className="question-spacing">
          <label className="form-label">{field.question}</label>
          {field.description && (
            <div className="description">{field.description}</div>
          )}
          <NameRecordingInput
            name={name}
            values={values}
            touched={touched}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
          />
        </div>
      );

    case 'geolocation':
      return (
        <Geolocator
          field={field}
          name={name}
          values={values}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
        />
      );

    default:
      console.warn(`Unknown or missing field type: ${JSON.stringify(field)}`);
      return null;
  }
};

// Generate about form format from form data
export const generateAboutFormFormat = (
  formData: FormField[],
  t: TranslationFunction,
) => {
  if (!formData) {
    return [];
  }

  return formData.map(field => ({
    name: field.id,
    input: AboutFormFieldRenderer,
    field,
    validation: generateFieldValidation(field, t),
  }));
};

// Generate validation for individual field
const generateFieldValidation = (
  field: FormField,
  t: TranslationFunction,
) => {
  switch (field.type) {
    case 'text':
      return Yup.string()
        .trim()
        .required(field.errorMessage || t('This field is required', {forceString: true}));

    case 'longtext':
      return Yup.string()
        .trim()
        .required(field.errorMessage || t('This field is required', {forceString: true}));

    case 'numeric':
      return Yup.string()
        .trim()
        .required(field.errorMessage || t('This field is required', {forceString: true}));

    case 'multicheckbox': {
      let schema = Yup.array().of(Yup.string());
      if (field.hasOther) {
        // If the applicant engaged "Other" (any non-predefined entry exists),
        // its write-in text must be non-empty.
        schema = schema.test(
          'other-not-empty',
          t('Please fill in the "Other" option or uncheck it', {
            forceString: true,
          }),
          value =>
            (value ?? [])
              .filter(entry => !field.options.includes(entry ?? ''))
              .every(entry => (entry ?? '').trim().length > 0),
        );
      }
      if (field.requireAll) {
        // Require every predefined option to be checked ("Other" is optional).
        schema = schema.test(
          'all-options-checked',
          field.errorMessage ||
            t('Please check every option to continue', {forceString: true}),
          value =>
            field.options.every(
              option => Array.isArray(value) && value.includes(option),
            ),
        );
      }
      return schema;
    }

    case 'singlecheckbox':
      return Yup.array()
        .of(Yup.string())
        .length(1, field.errorMessage || t('This field is required', {forceString: true}))
        .required(field.errorMessage || t('This field is required', {forceString: true}));

    case 'dropdown': {
      const baseValidation = Yup.string().required(
        field.errorMessage || t('Please select an option', {forceString: true}),
      );

      // Add stricter validation for gender field to ensure value is one of the allowed options
      if (field.id === GenderQuestionId) {
        return baseValidation.oneOf(
          Array.from(GENDER_OPTIONS),
          t('Please select a valid gender option', {forceString: true}),
        );
      }

      return baseValidation;
    }

    case 'radio':
      return Yup.string().required(
        field.errorMessage || t('Please select an option', {forceString: true}),
      );

    case 'linear_scale':
      return Yup.string().required(
        field.errorMessage || t('Please select a value', {forceString: true}),
      );

    case 'date': {
      return Yup.object({
        year: Yup.number()
          .integer(t('Year must be a four digit number.', {forceString: true}))
          .min(
            1900,
            t('Birth year is before 1900. Make sure it is four digits long.', {forceString: true}),
          )
          .required(t('Year is required in date of birth', {forceString: true})),
        month: Yup.number()
          .integer(t('Month must be a number.', {forceString: true}))
          .min(1, t('Month value must be greater than or equal to 1', {forceString: true}))
          .max(12, t('Month value must be less than or equal to 12', {forceString: true}))
          .required(t('Month is required in date of birth', {forceString: true})),
        day: Yup.number()
          .integer(t('Day must be a number.', {forceString: true}))
          .min(1, t('Day value must be greater than or equal to 1', {forceString: true}))
          .max(31, t('Day value must be less than or equal to 31', {forceString: true}))
          .required(t('Day is required in date of birth', {forceString: true})),
      })
        .required(t('Date of birth is required', {forceString: true}))
        .test(
          'minimum-age',
          // States the requirement outright, unlike the section-placement copy
          // this branch removed. That copy was advisory - it told an eligible
          // applicant a rule they could dodge by retyping the date. This one
          // ends the application, so a vague message would just leave a child
          // retrying forever with no idea why. The FTC's neutral-age-screen
          // guidance is about not announcing the threshold before asking, not
          // about withholding the reason afterwards.
          t('You must be at least {{minAge}} years old to apply', {
            substitutions: {minAge: MINIMUM_APPLICANT_AGE},
            forceString: true,
          }),
          dob => {
            if (!isCompleteDateOfBirth(dob)) {
              // Let the field-level required/format validators above report
              // the error; nothing valid to compute an age from yet.
              return true;
            }
            return getAgeFromDateOfBirth({dob}) >= MINIMUM_APPLICANT_AGE;
          },
        );
    }

    case 'text_display':
      // No validation needed for display fields
      return Yup.mixed().notRequired();
    case 'audio_input':
      return Yup.mixed().notRequired();
    case 'geolocation':
      return Yup.object({
        locality: Yup.string()
          .required(t('Location is required', {forceString: true}))
          .min(1, t('Location cannot be empty', {forceString: true}))
          .test('not-unknown', t('Please select a valid location', {forceString: true}), value => {
            return value !== 'Unknown Location';
          }),
        // Places supplies the country for a resolved entry; a typed one has
        // no source for it but the applicant.
        country: Yup.string().when('isManualEntry', {
          is: true,
          then: schema =>
            schema.required(t('Country is required', {forceString: true})),
          otherwise: schema => schema.optional(),
        }),
        coordinates: Yup.string().optional(),
        isManualEntry: Yup.boolean().optional(),
      })
        .nullable()
        .required(t('Location is required', {forceString: true}));
    default:
      console.warn(`Unknown or missing field type for validation: ${JSON.stringify(field)}`);
      return Yup.mixed().notRequired();
  }
};

// Generate default values for form fields
export const generateAboutFormDefaultValues = formData => {
  if (!formData) {
    return {};
  }

  const defaultValues = {};

  formData.forEach(field => {
    switch (field.type) {
      case 'text':
      case 'longtext':
      case 'numeric':
        defaultValues[field.id] = '';
        break;

      case 'multicheckbox':
      case 'singlecheckbox':
        defaultValues[field.id] = [];
        break;

      case 'dropdown':
      case 'radio':
      case 'linear_scale':
        defaultValues[field.id] = '';
        break;

      case 'date':
        // Key by the field's own id (e.g. 'dob') so getInitialValues, which
        // looks values up by field id, actually applies this default. Start
        // blank rather than a real-looking date: a prefilled DOB would let an
        // applicant submit without entering their own, bypassing the age gate
        // (required + minimum-age validation in generateFieldValidation).
        defaultValues[field.id] = {year: '', month: '', day: ''};
        break;

      case 'text_display':
      case 'audio_input':
      case 'geolocation':
        break;

      default:
        console.warn(`Unknown field type for default values: ${field.type}`);
    }
  });

  return defaultValues;
};
